'use client'

import { useRef } from 'react'
import { motion, useInView, useScroll, useTransform } from 'motion/react'
import { Sprout, Users, Award, Heart } from 'lucide-react'
import { ImageWithFallback } from './ImageWithFallback'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

export function ModernStory() {
  const ref = useRef<HTMLDivElement | null>(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const y = useTransform(scrollYProgress, [0, 1], [100, -100])
  const { language } = useLanguage()
  const copy = language === 'fr' ? fr : en

  const values = language === 'fr'
    ? [
        { icon: Sprout, title: 'Cultivation durable', description: "Méthodes exigeantes et respectueuses de l'environnement." },
        { icon: Users, title: 'Savoir-faire', description: 'Transmission du patrimoine agricole tunisien.' },
        { icon: Award, title: 'Qualité premium', description: 'Sélection rigoureuse, pressage à froid et traçabilité.' },
        { icon: Heart, title: 'Passion', description: 'Une histoire familiale au service de l’excellence.' },
      ]
    : [
        { icon: Sprout, title: 'Sustainable farming', description: 'Rigorous methods that respect the land and nature.' },
        { icon: Users, title: 'Know-how', description: 'A legacy passed through generations.' },
        { icon: Award, title: 'Premium quality', description: 'Strict selection, cold extraction, full traceability.' },
        { icon: Heart, title: 'Passion', description: 'A family story committed to excellence.' },
      ]

  const story = copy.story

  return (
    <section id="story" ref={ref} className="overflow-hidden bg-gradient-to-b from-background to-accent/10 py-24">
      <div className="mx-auto flex max-w-7xl flex-col gap-12 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-12">
          {/* Header */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="mb-4 inline-block rounded-full bg-primary/10 px-4 py-2 text-sm text-primary">{story.label}</span>
            <h2 className="mb-2 text-4xl tracking-tight sm:text-5xl lg:text-6xl">{story.title}</h2>
            <p className="text-lg font-semibold text-primary">{story.subtitle}</p>
          </motion.div>

          {/* Full-width image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div className="overflow-hidden rounded-3xl shadow-2xl" style={{ y }}>
              <ImageWithFallback
                src="/history-salamdor.png"
                alt="Tunisia landscape"
                className="w-full object-contain"
              />
            </motion.div>
          </motion.div>

          {/* Badge + Text row */}
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
            {/* Badge - now inline, not floating */}
            <motion.div
              className="shrink-0 rounded-2xl border border-border bg-card p-6 shadow-xl lg:w-64"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="flex items-center space-x-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Award className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-3xl text-primary">50+</p>
                  <p className="text-sm text-muted-foreground">{story.experienceLabel}</p>
                </div>
              </div>
            </motion.div>

            {/* Text content - wider */}
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                {(story.paragraphs || []).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((value, index) => (
            <motion.div key={value.title} className="group h-full" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}>
              <div className="flex h-full flex-col rounded-2xl border border-border bg-card/50 p-6 transition-all duration-300 hover:border-primary/20 hover:bg-card backdrop-blur-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors group-hover:bg-primary/20">
                  <value.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2">{value.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{value.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
